import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOnDarkSection, setIsOnDarkSection] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);
      
      // Hero section is viewport height, exit dark section when scrolling past 80% of viewport
      const heroHeight = window.innerHeight;
      setIsOnDarkSection(scrollY < heroHeight * 0.75);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      setIsOnDarkSection(false);
    } else {
      setIsOnDarkSection(true);
    }
  }, [location]);

  const navLinks = [
    { name: 'Løsninger', path: '/loesninger' },
    { name: 'Cases', path: '/cases' },
    { name: 'For Designere', path: '/for-designere' },
    { name: 'Om os', path: '/om-os' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  const isDark = location.pathname === '/' && isOnDarkSection;
  const showGlass = isScrolled && !isDark;
  const textDark = !isDark;

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'py-4' : 'py-6'
        } ${showGlass ? 'glass-light' : ''}`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between">
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_82f36689-5905-4eb9-a410-52f80a0daaf2/artifacts/1brex7h8_Logo%20b%C3%A6otlagt.png"
              alt="NeonSign - LED Technology & Signs"
              className={`h-10 md:h-12 w-auto transition-all duration-300 ${
                textDark ? '' : 'brightness-0 invert'
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '')}`}
                className={`text-xs font-medium tracking-[0.15em] uppercase link-underline transition-colors duration-300 ${
                  textDark 
                    ? location.pathname === link.path
                      ? 'text-[#1A1A1A]'
                      : 'text-[#666666] hover:text-[#1A1A1A]'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${textDark ? 'text-[#1A1A1A]' : 'text-white'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-white z-[100] flex flex-col justify-center items-center" data-testid="mobile-menu">
          <button
            className="absolute top-6 right-6 p-2 text-[#1A1A1A]"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X size={24} />
          </button>
          <nav className="flex flex-col items-center gap-8">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.path.replace('/', '')}`}
                className="text-2xl font-heading font-light tracking-[0.1em] uppercase text-[#1A1A1A] animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {link.name}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </>
  );
};
